import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Form, Input, Select, Table } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import SvgIcon from '@/components/SvgIcon';
import { renderDateTime, renderNumRate, toTsOptions } from '@/utils/common';
import { getTenantList } from '@/services/kopTenantService';
import ShadowCard from '@/components/ShadowCard';
import ColTip from '@/components/Tip';
import PopupLink from '@/components/Cluster/PopupLink';
import PermWrapper from '@/components/PermWrapper';
import TopicTip from '@/components/Cluster/TopicTip';
import FormModal from '../FormModal';
import Dashboard from '../Dashboard';
import Detail from './Detail';

const { FormItem } = Form;

function Body({ tenantOptions, clusterName }) {
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [formVisible, setFormVisible] = useState(false);
  const navigate = useNavigate();

  const columns = [
    {
      colKey: 'tenant',
      title: '租户',
      fixed: 'left',
      cell: ({ row }) => (
        <Detail clusterName={clusterName} tenant={row.tenant} onDelete={doRefresh} />
      ),
      width: 200,
      sorter: true,
    },
    {
      colKey: 'namespaceCount',
      title: '命名空间',
      sorter: true,
      width: 120,
      cell: ({ row }) => (
        <PopupLink
          tip='跳转命名空间'
          onClick={() => {
            navigate(`/kop-cluster/namespace/list?name=${clusterName}&tenant=${row.tenant}`);
          }}
        >
          {row.namespaceCount}
        </PopupLink>
      ),
    },
    {
      colKey: 'topicPartitionCount',
      sorter: true,
      title: <TopicTip>Topic</TopicTip>,
      cell: ({ row }) => {
        const showLink = row.topicPartitionCount && row.topicCount;
        const content = `${row.topicPartitionCount}/${row.topicCount}`;
        return showLink ? (
          <PopupLink
            tip='跳转Topic'
            onClick={() => {
              navigate(`/kop-cluster/topic/list?name=${clusterName}&tenant=${row.tenant}`);
            }}
          >
            {content}
          </PopupLink>
        ) : (
          content
        );
      },
      width: 110,
    },
    {
      colKey: 'createByPlatform',
      title: '平台创建',
      sorter: true,
      cell: ({ row }) => (row.createByPlatform ? '是' : '否'),
      width: 100,
    },
    // {
    //   title: '生产吞吐',
    //   colKey: 'bytesIn',
    //   sortType: 'all',
    //   sorter: true,
    //   width: 120,
    //   cell: renderByteRate,
    // },
    // {
    //   title: '消费吞吐',
    //   colKey: 'bytesOut',
    //   sortType: 'all',
    //   sorter: true,
    //   width: 120,
    //   cell: renderByteRate,
    // },
    {
      title: '生产速率',
      colKey: 'rateIn',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderNumRate,
    },
    {
      title: '消费速率',
      colKey: 'rateOut',
      sortType: 'all',
      sorter: true,
      width: 120,
      cell: renderNumRate,
    },
    {
      title: '创建时间',
      sorter: true,
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '归属应用',
      defaultHide: true,
      sorter: true,
      colKey: 'app',
      width: 120,
    },
    {
      colKey: 'updateTime',
      sorter: true,
      cell: renderDateTime,
      title: <ColTip content='非实时，有分钟级延迟'>采集时间</ColTip>,
      width: 180,
    },
    {
      colKey: 'note',
      title: '备注',
      ellipsis: true,
      defaultHide: true,
      width: 260,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Icon
          type='presentation-chart-line'
          size={18}
          onClick={() => {
            setVisible(true);
            setCurrentRow(row);
          }}
        />
      ),
      width: 60,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getTenantList({ ...params, clusterName });
    setData(data);
  });

  const { header, config, setData, doRefresh, doSearch } = useTable({
    search: (
      <>
        <FormItem name='tenant'>
          <Select options={toTsOptions(tenantOptions)} placeholder='请选择租户' clearable />
        </FormItem>
        <FormItem name='app'>
          <Input placeholder='请输入应用名称' clearable />
        </FormItem>
      </>
    ),
    columns,
    onSearch: loadData,
    operators: (
      <PermWrapper resource='kop:tenant:list:create'>
        <Button
          theme='primary'
          onClick={() => {
            setFormVisible(true);
          }}
        >
          创建租户
        </Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    if (clusterName) {
      doSearch();
    }
  }, [clusterName, doSearch]);

  const handleClose = () => {
    setVisible(false);
  };

  const closeFormModal = (refresh) => {
    setFormVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        visible={visible}
        detail={currentRow}
        handleClose={handleClose}
      />
      <FormModal clusterName={clusterName} visible={formVisible} handleClose={closeFormModal} />
    </Root>
  );
}

export default connect((state) => ({
  tenantOptions: state.kopTenant.tenantOptions,
}))(Body);

const Root = styled(ShadowCard)`
  flex: 1;
  margin-top: 12px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
